import React, { useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Descriptions,
  Dropdown,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Steps,
  Typography,
  message,
} from "antd";
import {
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  ExperimentOutlined,
  MoreOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { payrollAmountInputProps } from "./payrollAmountInput";
import dayjs from "dayjs";

const { Text, Title } = Typography;
const dateValue = (value) => (value ? dayjs(value) : null);
const isoDate = (value) => (value ? value.format("YYYY-MM-DD") : null);
const statusColor = {
  Draft: "default",
  Tested: "cyan",
  Approved: "blue",
  Active: "success",
  Retired: "volcano",
};
const enabledValue = (value) => value !== false && value !== "false";
const splitTaxBands = (bands = []) => ({
  residentTaxBands: bands.filter((band) => band.residencyStatus === "Resident"),
  nonResidentTaxBands: bands.filter(
    (band) => band.residencyStatus === "Non-Resident",
  ),
});
const TEST_RESULT_FIELDS = [
  ["paye", "PAYE"],
  ["chargeableIncome", "Chargeable income"],
  ["employeeNssf", "Employee NSSF"],
  ["employerNssf", "Employer NSSF"],
  ["lst", "LST"],
  ["netPay", "Net pay"],
];
const errorText = (value) => {
  if (Array.isArray(value)) return value.map(errorText).join(" ");
  if (value && typeof value === "object") {
    return Object.entries(value)
      .map(([key, item]) => `${key}: ${errorText(item)}`)
      .join(" ");
  }
  return String(value || "");
};
const stepFields = [
  ["name", "countryCode", "currency", "effectiveFrom"],
  ["nssfEnabled", "nssfEmployeeRate", "nssfEmployerRate", "nssfBasis"],
  ["lstEnabled", "lstIncomeBasis", "lstInstallmentMonths", "lstBands"],
  ["residentTaxBands", "nonResidentTaxBands"],
  ["testCases"],
];

const PayrollStatutoryRulesTab = ({
  rules,
  saving,
  fetchUgandaDefaults,
  saveRule,
  deleteRule,
  copyVersion,
  ruleAction,
  testDraftRule,
}) => {
  const [form] = Form.useForm();
  const [copyForm] = Form.useForm();
  const [simulateForm] = Form.useForm();
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [copySource, setCopySource] = useState(null);
  const [simulateRule, setSimulateRule] = useState(null);
  const [simulation, setSimulation] = useState(null);
  const [historyRule, setHistoryRule] = useState(null);
  const [loadingDefaults, setLoadingDefaults] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [draftTestResults, setDraftTestResults] = useState(null);
  const [draftTestError, setDraftTestError] = useState(null);
  const [testingDraft, setTestingDraft] = useState(false);
  const nssfEnabled = enabledValue(
    Form.useWatch("nssfEnabled", { form, preserve: true }),
  );
  const payeEnabled = enabledValue(
    Form.useWatch("payeEnabled", { form, preserve: true }),
  );
  const lstEnabled = enabledValue(
    Form.useWatch("lstEnabled", { form, preserve: true }),
  );
  const effectiveFrom = Form.useWatch("effectiveFrom", form);
  const testCases = Form.useWatch("testCases", form) || [];

  const buildPayload = (values) => ({
    ...values,
    effectiveFrom: isoDate(values.effectiveFrom),
    effectiveTo: isoDate(values.effectiveTo),
    nssfEnabled: enabledValue(values.nssfEnabled),
    payeEnabled: enabledValue(values.payeEnabled),
    lstEnabled: enabledValue(values.lstEnabled),
    nssfEmployeeRate: enabledValue(values.nssfEnabled)
      ? values.nssfEmployeeRate
      : 0,
    nssfEmployerRate: enabledValue(values.nssfEnabled)
      ? values.nssfEmployerRate
      : 0,
    nssfLowerLimit: enabledValue(values.nssfEnabled)
      ? values.nssfLowerLimit || null
      : null,
    nssfUpperLimit: enabledValue(values.nssfEnabled)
      ? values.nssfUpperLimit || null
      : null,
    taxBands: [
      ...(values.residentTaxBands || []).map((band) => ({
        ...band,
        residencyStatus: "Resident",
      })),
      ...(values.nonResidentTaxBands || []).map((band) => ({
        ...band,
        residencyStatus: "Non-Resident",
      })),
    ].map((band, index) => ({
      ...band,
      upperBound: band.upperBound ?? null,
      sortOrder: index + 1,
    })),
    lstInstallmentCount: enabledValue(values.lstEnabled)
      ? (values.lstInstallmentMonths || [10]).length
      : 1,
    lstInstallmentMonths: enabledValue(values.lstEnabled)
      ? values.lstInstallmentMonths || [10]
      : [10],
    lstBands: enabledValue(values.lstEnabled)
      ? (values.lstBands || []).map((band, index) => ({
          ...band,
          upperBound: band.upperBound ?? null,
          sortOrder: index + 1,
        }))
      : [],
    testCases: (values.testCases || []).map((test, index) => ({
      ...test,
      payeEnabled: enabledValue(test.payeEnabled),
      otherDeductions: test.otherDeductions || 0,
      expectedLst: test.expectedLst || 0,
      sortOrder: index + 1,
    })),
  });

  const showForm = async (rule = null) => {
    setSelected(rule);
    setCurrentStep(0);
    setDraftTestResults(null);
    setDraftTestError(null);
    form.resetFields();
    if (rule) {
      form.setFieldsValue({
        ...rule,
        nssfEnabled: enabledValue(rule.nssfEnabled),
        payeEnabled: enabledValue(rule.payeEnabled),
        lstEnabled: enabledValue(rule.lstEnabled),
        ...splitTaxBands(rule.taxBands),
        effectiveFrom: dateValue(rule.effectiveFrom),
        effectiveTo: dateValue(rule.effectiveTo),
      });
      setOpen(true);
      return;
    }
    setLoadingDefaults(true);
    const defaults = await fetchUgandaDefaults();
    setLoadingDefaults(false);
    if (!defaults) return;
    form.setFieldsValue({
      ...defaults,
      nssfEnabled: enabledValue(defaults.nssfEnabled),
      payeEnabled: enabledValue(defaults.payeEnabled),
      lstEnabled: enabledValue(defaults.lstEnabled),
      ...splitTaxBands(defaults.taxBands),
      effectiveFrom: dateValue(defaults.effectiveFrom),
      effectiveTo: dateValue(defaults.effectiveTo),
    });
    setOpen(true);
  };

  const submit = async () => {
    const values = await form.validateFields();
    const payload = buildPayload(values);
    delete payload.residentTaxBands;
    delete payload.nonResidentTaxBands;
    const result = await saveRule(selected?.ruleSetId, payload);
    if (result) setOpen(false);
  };

  const handleRuleToggle = (field, checked) => {
    setDraftTestResults(null);
    setDraftTestError(null);
    if (checked) return;
    const tests = form.getFieldValue("testCases") || [];
    form.setFieldValue(
      "testCases",
      tests.map((test) => ({
        ...test,
        ...(field === "payeEnabled"
          ? { payeEnabled: false, expectedPaye: 0 }
          : {}),
        ...(field === "nssfEnabled"
          ? {
              expectedEmployeeNssf: 0,
              expectedEmployerNssf: 0,
            }
          : {}),
        ...(field === "lstEnabled"
          ? {
              expectedLst: 0,
              expectedChargeableIncome: test.grossEarnings || 0,
            }
          : {}),
      })),
    );
  };

  const useSimulatedValuesAsExpected = () => {
    const tests = form.getFieldValue("testCases") || [];
    const resultsByIndex = new Map(
      (draftTestResults || []).map((result) => [
        Number(result.testCaseId),
        result,
      ]),
    );
    form.setFieldValue(
      "testCases",
      tests.map((test, index) => {
        const actual = resultsByIndex.get(index)?.actual;
        if (!actual) return test;
        return {
          ...test,
          expectedPaye: Number(actual.paye),
          expectedChargeableIncome: Number(actual.chargeableIncome),
          expectedEmployeeNssf: Number(actual.employeeNssf),
          expectedEmployerNssf: Number(actual.employerNssf),
          expectedLst: Number(actual.lst),
          expectedNetPay: Number(actual.netPay),
        };
      }),
    );
    setDraftTestResults(null);
    message.success(
      "Expected values updated from the simulation. Run the tests again to confirm.",
    );
  };

  const validateStep = async (step) => {
    if (
      (step === 1 && !nssfEnabled) ||
      (step === 2 && !lstEnabled) ||
      (step === 3 && !payeEnabled)
    )
      return true;
    try {
      await form.validateFields(stepFields[step]);
      return true;
    } catch (error) {
      message.warning(
        "Complete the required fields on this step before continuing.",
      );
      return false;
    }
  };

  const changeStep = async (target) => {
    if (target <= currentStep) {
      setCurrentStep(target);
      return;
    }
    for (let step = currentStep; step < target; step += 1) {
      if (!(await validateStep(step))) {
        setCurrentStep(step);
        return;
      }
    }
    setCurrentStep(target);
  };

  const runDraftTests = async () => {
    try {
      // Hidden wizard steps preserve their values, but Ant Design validation
      // can reject unmounted fields before the API has a chance to explain
      // what is missing. Send the complete form state to the same backend
      // validator used when saving the rule set.
      const values = form.getFieldsValue(true);
      const payload = buildPayload(values);
      delete payload.residentTaxBands;
      delete payload.nonResidentTaxBands;
      setTestingDraft(true);
      setDraftTestError(null);
      const result = await testDraftRule(payload);
      if (result?.ok === false) {
        setDraftTestResults(null);
        setDraftTestError(result);
      } else if (result) {
        setDraftTestResults(result.results || []);
      }
    } catch (error) {
      setDraftTestError({
        detail:
          error?.message ||
          "Complete the rule configuration before running tests.",
      });
    } finally {
      setTestingDraft(false);
    }
  };

  const submitCopy = async () => {
    const values = await copyForm.validateFields();
    const result = await copyVersion(copySource.ruleSetId, {
      name: values.name,
      effectiveFrom: isoDate(values.effectiveFrom),
      effectiveTo: isoDate(values.effectiveTo),
    });
    if (result) setCopySource(null);
  };

  const openSimulator = (rule) => {
    setSimulateRule(rule);
    setSimulation(null);
    simulateForm.resetFields();
    simulateForm.setFieldsValue({
      residencyStatus: "Resident",
      basicSalary: 2500000,
      grossEarnings: 2500000,
      nssfEnabled: true,
      lstEnabled: true,
      lstMonth: 10,
      otherDeductions: 0,
    });
  };

  const simulate = async () => {
    const values = await simulateForm.validateFields();
    const result = await ruleAction(simulateRule.ruleSetId, "simulate", values);
    if (result) setSimulation(result);
  };

  const performMenuAction = async (key, row) => {
    if (key === "edit") return showForm(row);
    if (key === "copy") {
      setCopySource(row);
      copyForm.resetFields();
      copyForm.setFieldsValue({
        name: row.name,
        effectiveFrom: row.effectiveTo
          ? dayjs(row.effectiveTo).add(1, "day")
          : dayjs().add(1, "day"),
      });
      return null;
    }
    if (key === "simulate") return openSimulator(row);
    if (key === "history") return setHistoryRule(row);
    return ruleAction(row.ruleSetId, key);
  };

  const menuItems = (row) => [
    ...(row.status === "Draft"
      ? [
          { key: "edit", label: "Edit draft", icon: <EditOutlined /> },
          {
            key: "runTests",
            label: "Run saved tests",
            icon: <ExperimentOutlined />,
          },
          { key: "markTested", label: "Mark as Tested" },
        ]
      : []),
    ...(row.status === "Tested"
      ? [{ key: "approve", label: "Approve rules" }]
      : []),
    ...(row.status === "Approved"
      ? [{ key: "activate", label: "Activate rules" }]
      : []),
    ...(row.status === "Active"
      ? [{ key: "retire", label: "Retire rules", danger: true }]
      : []),
    { key: "simulate", label: "Open simulator", icon: <ExperimentOutlined /> },
    { key: "history", label: "View lifecycle history" },
    { key: "copy", label: "Copy as new version", icon: <CopyOutlined /> },
  ];

  const columns = [
    {
      title: "Rule version",
      render: (_, row) => (
        <Space direction="vertical" size={0}>
          <Text strong>{row.name}</Text>
          <Text type="secondary">
            {row.countryCode} · {row.currency} · v{row.versionNumber}
          </Text>
        </Space>
      ),
    },
    {
      title: "Effective period",
      render: (_, row) =>
        `${dayjs(row.effectiveFrom).format("DD MMM YYYY")} — ${row.effectiveTo ? dayjs(row.effectiveTo).format("DD MMM YYYY") : "No end date"}`,
    },
    {
      title: "PAYE tests",
      render: (_, row) => {
        const passed =
          row.testCases?.filter((test) => test.lastPassed).length || 0;
        return (
          <Text>
            {passed}/{row.testCases?.length || 0} passing
          </Text>
        );
      },
    },
    {
      title: "NSSF",
      render: (_, row) =>
        row.nssfEnabled ? (
          <Text>
            {Number(row.nssfEmployeeRate)}% employee +{" "}
            {Number(row.nssfEmployerRate)}% employer
          </Text>
        ) : (
          <Tag>Disabled</Tag>
        ),
    },
    {
      title: "LST",
      render: (_, row) =>
        row.lstEnabled ? (
          <Space direction="vertical" size={0}>
            <Text>
              {(row.lstInstallmentMonths || [])
                .map((month) =>
                  dayjs()
                    .month(month - 1)
                    .format("MMM"),
                )
                .join(", ")}
            </Text>
            <Text type="secondary">{row.lstIncomeBasis}</Text>
          </Space>
        ) : (
          <Tag>Disabled</Tag>
        ),
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (value) => <Tag color={statusColor[value]}>{value}</Tag>,
    },
    {
      title: "Actions",
      width: 90,
      align: "center",
      render: (_, row) => (
        <Space size={0}>
          <Dropdown
            menu={{
              items: menuItems(row),
              onClick: ({ key }) => performMenuAction(key, row),
            }}
            trigger={["click"]}
          >
            <Button
              type="text"
              size="small"
              icon={<MoreOutlined />}
              loading={saving}
            />
          </Dropdown>
          {row.status !== "Active" && (
            <Popconfirm
              title="Delete this unused rule version?"
              description="Deletion is allowed only when no payroll run uses this version."
              okText="Delete"
              okButtonProps={{ danger: true }}
              onConfirm={() => deleteRule(row.ruleSetId)}
            >
              <Button
                type="text"
                size="small"
                danger
                icon={<DeleteOutlined />}
              />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      <div style={{ textAlign: "right" }}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          loading={loadingDefaults}
          onClick={() => showForm()}
        >
          Add draft
        </Button>
      </div>
      <Table
        rowKey="ruleSetId"
        columns={columns}
        dataSource={rules}
        scroll={{ x: 1050 }}
      />

      <Modal
        title={
          selected
            ? `Edit ${selected.name} v${selected.versionNumber}`
            : "Add payroll rules"
        }
        open={open}
        onCancel={() => setOpen(false)}
        confirmLoading={saving || testingDraft}
        width={1180}
        footer={
          <Space>
            <Button
              onClick={() => changeStep(currentStep - 1)}
              disabled={currentStep === 0}
            >
              Back
            </Button>
            {currentStep < 4 && (
              <Button
                type="primary"
                onClick={() => changeStep(currentStep + 1)}
              >
                Next
              </Button>
            )}
            {currentStep === 4 && (
              <>
                <Button
                  icon={<ExperimentOutlined />}
                  onClick={runDraftTests}
                  loading={testingDraft}
                >
                  Run Tests
                </Button>
                <Button type="primary" onClick={submit} loading={saving}>
                  Save Draft
                </Button>
              </>
            )}
          </Space>
        }
      >
        <Steps
          current={currentStep}
          onChange={changeStep}
          responsive
          style={{ marginBottom: 24 }}
          items={[
            { title: "Overview" },
            { title: nssfEnabled ? "NSSF" : "NSSF (Disabled)" },
            { title: lstEnabled ? "LST" : "LST (Disabled)" },
            { title: payeEnabled ? "PAYE Bands" : "PAYE (Disabled)" },
            { title: "Tests & Review" },
          ]}
        />
        <Form form={form} layout="vertical">
          {currentStep === 0 && (
            <>
              <Title level={5}>Rule Version</Title>
              <Row gutter={16}>
                <Col xs={24} md={8}>
                  <Form.Item
                    name="name"
                    label="Name"
                    rules={[{ required: true }]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
                <Col xs={12} md={4}>
                  <Form.Item
                    name="countryCode"
                    label="Country code"
                    rules={[{ required: true }]}
                  >
                    <Input maxLength={2} />
                  </Form.Item>
                </Col>
                <Col xs={12} md={4}>
                  <Form.Item
                    name="currency"
                    label="Currency"
                    rules={[{ required: true }]}
                  >
                    <Input maxLength={3} />
                  </Form.Item>
                </Col>
                <Col xs={12} md={4}>
                  <Form.Item
                    name="nssfEnabled"
                    label="NSSF enabled"
                    valuePropName="checked"
                  >
                    <Switch
                      onChange={(checked) =>
                        handleRuleToggle("nssfEnabled", checked)
                      }
                    />
                  </Form.Item>
                </Col>
                <Col xs={12} md={4}>
                  <Form.Item
                    name="payeEnabled"
                    label="PAYE enabled"
                    valuePropName="checked"
                  >
                    <Switch
                      onChange={(checked) =>
                        handleRuleToggle("payeEnabled", checked)
                      }
                    />
                  </Form.Item>
                </Col>
                <Col xs={12} md={4}>
                  <Form.Item
                    name="lstEnabled"
                    label="LST enabled"
                    valuePropName="checked"
                  >
                    <Switch
                      onChange={(checked) =>
                        handleRuleToggle("lstEnabled", checked)
                      }
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={6}>
                  <Form.Item
                    name="effectiveFrom"
                    label="Effective from"
                    rules={[{ required: true }]}
                  >
                    <DatePicker style={{ width: "100%" }} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={6}>
                  <Form.Item name="effectiveTo" label="Effective to">
                    <DatePicker
                      disabledDate={(current) =>
                        Boolean(
                          effectiveFrom &&
                          current?.isBefore(effectiveFrom, "day"),
                        )
                      }
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </>
          )}

          {currentStep === 1 && (
            <>
              <Title level={5}>NSSF</Title>
              {nssfEnabled ? (
                <Row gutter={16}>
                  <Col xs={12} md={5}>
                    <Form.Item
                      name="nssfEmployeeRate"
                      label="Employee rate (%)"
                      rules={[{ required: true }]}
                    >
                      <InputNumber
                        min={0}
                        max={100}
                        precision={4}
                        style={{ width: "100%" }}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={12} md={5}>
                    <Form.Item
                      name="nssfEmployerRate"
                      label="Employer rate (%)"
                      rules={[{ required: true }]}
                    >
                      <InputNumber
                        min={0}
                        max={100}
                        precision={4}
                        style={{ width: "100%" }}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={6}>
                    <Form.Item
                      name="nssfBasis"
                      label="Contribution basis"
                      rules={[{ required: true }]}
                    >
                      <Select
                        options={[
                          "Gross Earnings",
                          "Taxable Earnings",
                          "Basic Salary",
                        ].map((value) => ({ value, label: value }))}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={12} md={4}>
                    <Form.Item name="nssfLowerLimit" label="Minimum basis">
                      <InputNumber
                        {...payrollAmountInputProps}
                        min={0}
                        style={{ width: "100%" }}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={12} md={4}>
                    <Form.Item name="nssfUpperLimit" label="Maximum basis">
                      <InputNumber
                        {...payrollAmountInputProps}
                        min={0}
                        style={{ width: "100%" }}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              ) : (
                <Alert
                  type="info"
                  showIcon
                  message="NSSF is disabled"
                  description="Employee and employer NSSF will both be UGX 0.00 for this rule set."
                />
              )}
            </>
          )}

          {currentStep === 2 && (
            <>
              <Title level={5}>Local Service Tax (LST)</Title>
              {lstEnabled ? (
                <>
                  <Row gutter={16}>
                    <Col xs={24} md={8}>
                      <Form.Item
                        name="lstIncomeBasis"
                        label="Annual LST assessment basis"
                        rules={[{ required: true }]}
                      >
                        <Select
                          options={[
                            {
                              value: "Prior June Gross",
                              label: "Prior finalized June gross",
                            },
                            {
                              value: "Current Payroll Gross",
                              label: "Current payroll gross",
                            },
                          ]}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item
                        name="lstInstallmentMonths"
                        label="Annual LST assessment months"
                        rules={[{ required: true }]}
                      >
                        <Select
                          mode="multiple"
                          options={Array.from({ length: 12 }, (_, index) => ({
                            value: index + 1,
                            label: dayjs().month(index).format("MMMM"),
                          }))}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={6}>
                      <Form.Item
                        name="lstFinancialYearStartMonth"
                        label="Financial year starts"
                      >
                        <Select
                          options={Array.from({ length: 12 }, (_, index) => ({
                            value: index + 1,
                            label: dayjs().month(index).format("MMMM"),
                          }))}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Form.List name="lstBands">
                    {(fields, { add, remove }) => (
                      <Space direction="vertical" style={{ width: "100%" }}>
                        {fields.map((field, index) => (
                          <Row gutter={8} key={field.key}>
                            <Col span={7}>
                              <Form.Item
                                name={[field.name, "lowerBound"]}
                                label={index === 0 ? "Income above (UGX)" : ""}
                                rules={[{ required: true }]}
                              >
                                <InputNumber
                                  {...payrollAmountInputProps}
                                  min={0}
                                  style={{ width: "100%" }}
                                />
                              </Form.Item>
                            </Col>
                            <Col span={7}>
                              <Form.Item
                                name={[field.name, "upperBound"]}
                                label={index === 0 ? "Up to (UGX)" : ""}
                              >
                                <InputNumber
                                  {...payrollAmountInputProps}
                                  min={0}
                                  style={{ width: "100%" }}
                                />
                              </Form.Item>
                            </Col>
                            <Col span={8}>
                              <Form.Item
                                name={[field.name, "annualLiability"]}
                                label={index === 0 ? "Annual LST (UGX)" : ""}
                                rules={[{ required: true }]}
                              >
                                <InputNumber
                                  {...payrollAmountInputProps}
                                  min={0}
                                  style={{ width: "100%" }}
                                />
                              </Form.Item>
                            </Col>
                            <Col span={2}>
                              <Button
                                type="text"
                                danger
                                icon={<DeleteOutlined />}
                                onClick={() => remove(field.name)}
                              />
                            </Col>
                          </Row>
                        ))}
                        <Button
                          type="dashed"
                          block
                          onClick={() => add({ annualLiability: 0 })}
                        >
                          Add LST band
                        </Button>
                      </Space>
                    )}
                  </Form.List>
                </>
              ) : (
                <Alert
                  type="info"
                  showIcon
                  message="LST is disabled"
                  description="Local Service Tax will be UGX 0.00 and will not reduce PAYE chargeable income."
                />
              )}
            </>
          )}

          {currentStep === 3 && (
            <>
              <Title level={5}>Monthly PAYE Bands</Title>
              {!payeEnabled ? (
                <Alert
                  type="info"
                  showIcon
                  message="PAYE is disabled"
                  description="PAYE will be UGX 0.00 for both resident and non-resident employees."
                />
              ) : (
                <>
                  <Alert
                    type="info"
                    style={{ marginBottom: 12 }}
                    message="PAYE = base tax + rate × (PAYE chargeable income − lower bound). Leave the final upper bound empty."
                  />
                  {[
                    ["residentTaxBands", "Resident PAYE bands"],
                    ["nonResidentTaxBands", "Non-resident PAYE bands"],
                  ].map(([listName, title]) => (
                    <Card
                      key={listName}
                      size="small"
                      title={title}
                      style={{ marginBottom: 12 }}
                    >
                      <Form.List name={listName}>
                        {(fields, { add, remove }) => (
                          <Space direction="vertical" style={{ width: "100%" }}>
                            {fields.map((field, index) => (
                              <Row gutter={8} key={field.key}>
                                <Col span={5}>
                                  <Form.Item
                                    name={[field.name, "lowerBound"]}
                                    label={index === 0 ? "From (UGX)" : ""}
                                    rules={[{ required: true }]}
                                  >
                                    <InputNumber
                                      {...payrollAmountInputProps}
                                      min={0}
                                      style={{ width: "100%" }}
                                    />
                                  </Form.Item>
                                </Col>
                                <Col span={5}>
                                  <Form.Item
                                    name={[field.name, "upperBound"]}
                                    label={index === 0 ? "To (UGX)" : ""}
                                  >
                                    <InputNumber
                                      {...payrollAmountInputProps}
                                      min={0}
                                      style={{ width: "100%" }}
                                    />
                                  </Form.Item>
                                </Col>
                                <Col span={6}>
                                  <Form.Item
                                    name={[field.name, "baseTax"]}
                                    label={index === 0 ? "Base tax" : ""}
                                    rules={[{ required: true }]}
                                  >
                                    <InputNumber
                                      {...payrollAmountInputProps}
                                      min={0}
                                      style={{ width: "100%" }}
                                    />
                                  </Form.Item>
                                </Col>
                                <Col span={6}>
                                  <Form.Item
                                    name={[field.name, "marginalRate"]}
                                    label={index === 0 ? "Rate (%)" : ""}
                                    rules={[{ required: true }]}
                                  >
                                    <InputNumber
                                      min={0}
                                      max={100}
                                      precision={4}
                                      style={{ width: "100%" }}
                                    />
                                  </Form.Item>
                                </Col>
                                <Col span={2}>
                                  <Button
                                    type="text"
                                    danger
                                    icon={<DeleteOutlined />}
                                    onClick={() => remove(field.name)}
                                  />
                                </Col>
                              </Row>
                            ))}
                            <Button
                              type="dashed"
                              block
                              onClick={() =>
                                add({ baseTax: 0, marginalRate: 0 })
                              }
                            >
                              Add {title.replace(" bands", "")} band
                            </Button>
                          </Space>
                        )}
                      </Form.List>
                    </Card>
                  ))}
                </>
              )}
            </>
          )}

          {currentStep === 4 && (
            <>
              <Title level={5} style={{ marginTop: 20 }}>
                Test Cases & Review
              </Title>
              {(!nssfEnabled || !payeEnabled || !lstEnabled) && (
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginBottom: 12 }}
                  message="Some statutory calculations are disabled"
                  description={`The simulation will return zero for ${[
                    !nssfEnabled && "NSSF",
                    !payeEnabled && "PAYE",
                    !lstEnabled && "LST",
                  ]
                    .filter(Boolean)
                    .join(
                      ", ",
                    )}. Existing expected values may still describe the enabled setup, so a failed test means those expectations are now stale. Run the tests, review the variances, then use “Use Simulated Values as Expected” if the disabled setup is intentional.`}
                />
              )}
              <Form.List name="testCases">
                {(fields, { add, remove }) => (
                  <Space direction="vertical" style={{ width: "100%" }}>
                    {fields.map((field, index) => (
                      <Card
                        key={field.key}
                        size="small"
                        title={
                          testCases[index]?.name ||
                          "Untitled Statutory Scenario"
                        }
                        extra={
                          <Button
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => remove(field.name)}
                          />
                        }
                      >
                        <Card
                          type="inner"
                          size="small"
                          title="Scenario inputs"
                          style={{ marginBottom: 12 }}
                        >
                          <Row gutter={12}>
                            <Col xs={24} lg={8}>
                              <Form.Item
                                name={[field.name, "name"]}
                                label="Test name"
                                rules={[{ required: true }]}
                              >
                                <Input />
                              </Form.Item>
                            </Col>
                            <Col xs={24} sm={12} lg={4}>
                              <Form.Item
                                name={[field.name, "residencyStatus"]}
                                label="Residency"
                              >
                                <Select
                                  options={["Resident", "Non-Resident"].map(
                                    (value) => ({ value, label: value }),
                                  )}
                                />
                              </Form.Item>
                            </Col>
                            <Col xs={24} sm={12} lg={4}>
                              <Form.Item
                                name={[field.name, "basicSalary"]}
                                label="Basic salary"
                              >
                                <InputNumber
                                  {...payrollAmountInputProps}
                                  min={0}
                                  style={{ width: "100%" }}
                                />
                              </Form.Item>
                            </Col>
                            <Col xs={24} sm={12} lg={4}>
                              <Form.Item
                                name={[field.name, "grossEarnings"]}
                                label="Gross earnings"
                              >
                                <InputNumber
                                  {...payrollAmountInputProps}
                                  min={0}
                                  style={{ width: "100%" }}
                                />
                              </Form.Item>
                            </Col>
                            <Col xs={24} sm={12} lg={4}>
                              <Form.Item
                                name={[field.name, "otherDeductions"]}
                                label="Post-PAYE deductions"
                              >
                                <InputNumber
                                  {...payrollAmountInputProps}
                                  min={0}
                                  style={{ width: "100%" }}
                                />
                              </Form.Item>
                            </Col>
                          </Row>
                        </Card>

                        <Card
                          type="inner"
                          size="small"
                          title="Statutory settings"
                          style={{ marginBottom: 12 }}
                        >
                          <Row gutter={12} align="middle">
                            <Col xs={8} sm={4}>
                              <Form.Item
                                name={[field.name, "payeEnabled"]}
                                label="Apply PAYE"
                                valuePropName="checked"
                              >
                                <Switch disabled={!payeEnabled} />
                              </Form.Item>
                            </Col>
                            <Col xs={8} sm={4}>
                              <Form.Item
                                name={[field.name, "nssfEnabled"]}
                                label="Apply NSSF"
                                valuePropName="checked"
                              >
                                <Switch disabled={!nssfEnabled} />
                              </Form.Item>
                            </Col>
                            <Col xs={8} sm={4}>
                              <Form.Item
                                name={[field.name, "lstEnabled"]}
                                label="Apply LST"
                                valuePropName="checked"
                              >
                                <Switch disabled={!lstEnabled} />
                              </Form.Item>
                            </Col>
                            <Col xs={24} sm={12}>
                              <Form.Item
                                name={[field.name, "lstMonth"]}
                                label="Payroll month"
                              >
                                <Select
                                  options={Array.from(
                                    { length: 12 },
                                    (_, month) => ({
                                      value: month + 1,
                                      label: dayjs()
                                        .month(month)
                                        .format("MMMM"),
                                    }),
                                  )}
                                />
                              </Form.Item>
                            </Col>
                          </Row>
                        </Card>

                        <Card
                          type="inner"
                          size="small"
                          title="Expected results"
                        >
                          <Row gutter={12}>
                            <Col xs={24} sm={12} lg={4}>
                              <Form.Item
                                name={[field.name, "expectedPaye"]}
                                label="PAYE"
                              >
                                <InputNumber
                                  {...payrollAmountInputProps}
                                  min={0}
                                  style={{ width: "100%" }}
                                />
                              </Form.Item>
                            </Col>
                            <Col xs={24} sm={12} lg={4}>
                              <Form.Item
                                name={[field.name, "expectedChargeableIncome"]}
                                label="PAYE chargeable income"
                              >
                                <InputNumber
                                  {...payrollAmountInputProps}
                                  min={0}
                                  style={{ width: "100%" }}
                                />
                              </Form.Item>
                            </Col>
                            <Col xs={24} sm={12} lg={4}>
                              <Form.Item
                                name={[field.name, "expectedEmployeeNssf"]}
                                label="Employee NSSF"
                              >
                                <InputNumber
                                  {...payrollAmountInputProps}
                                  min={0}
                                  style={{ width: "100%" }}
                                />
                              </Form.Item>
                            </Col>
                            <Col xs={24} sm={12} lg={4}>
                              <Form.Item
                                name={[field.name, "expectedEmployerNssf"]}
                                label="Employer NSSF"
                              >
                                <InputNumber
                                  {...payrollAmountInputProps}
                                  min={0}
                                  style={{ width: "100%" }}
                                />
                              </Form.Item>
                            </Col>
                            <Col xs={24} sm={12} lg={4}>
                              <Form.Item
                                name={[field.name, "expectedLst"]}
                                label="LST"
                              >
                                <InputNumber
                                  {...payrollAmountInputProps}
                                  min={0}
                                  style={{ width: "100%" }}
                                />
                              </Form.Item>
                            </Col>
                            <Col xs={24} sm={12} lg={4}>
                              <Form.Item
                                name={[field.name, "expectedNetPay"]}
                                label="Net pay"
                              >
                                <InputNumber
                                  {...payrollAmountInputProps}
                                  min={0}
                                  style={{ width: "100%" }}
                                />
                              </Form.Item>
                            </Col>
                          </Row>
                        </Card>
                        {testCases[index]?.lastActual && (
                          <Table
                            style={{ marginTop: 12 }}
                            size="small"
                            pagination={false}
                            rowKey="field"
                            dataSource={[
                              ["paye", "PAYE"],
                              ["chargeableIncome", "Chargeable income"],
                              ["employeeNssf", "Employee NSSF"],
                              ["employerNssf", "Employer NSSF"],
                              ["lst", "LST"],
                              ["netPay", "Net pay"],
                            ].map(([fieldName, label]) => ({
                              field: fieldName,
                              label,
                              actual: testCases[index].lastActual[fieldName],
                              variance:
                                testCases[index].lastVariance?.[fieldName] ??
                                "—",
                            }))}
                            columns={[
                              { title: "Result", dataIndex: "label" },
                              { title: "Simulated", dataIndex: "actual" },
                              { title: "Variance", dataIndex: "variance" },
                            ]}
                          />
                        )}
                      </Card>
                    ))}
                    <Button
                      type="dashed"
                      block
                      onClick={() =>
                        add({
                          name: `New Statutory Scenario ${fields.length + 1}`,
                          residencyStatus: "Resident",
                          payeEnabled: true,
                          nssfEnabled: true,
                          lstEnabled: true,
                          lstMonth: 10,
                        })
                      }
                    >
                      Add Test Case
                    </Button>
                  </Space>
                )}
              </Form.List>

              {draftTestError && (
                <Alert
                  type="error"
                  showIcon
                  style={{ marginTop: 16 }}
                  message="Tests could not run"
                  description={
                    <Space direction="vertical">
                      <span>
                        {draftTestError.detail ||
                          "Complete the rule configuration before testing."}
                      </span>
                      {draftTestError.errors && (
                        <span>{errorText(draftTestError.errors)}</span>
                      )}
                    </Space>
                  }
                />
              )}
              {draftTestResults && (
                <Card
                  size="small"
                  style={{ marginTop: 16 }}
                  title="Unsaved Draft Test Results"
                  extra={
                    <Space>
                      <Tag
                        color={
                          draftTestResults.every((result) => result.passed)
                            ? "success"
                            : "error"
                        }
                      >
                        {
                          draftTestResults.filter((result) => result.passed)
                            .length
                        }
                        /{draftTestResults.length} Passed
                      </Tag>
                      {!draftTestResults.every((result) => result.passed) && (
                        <Button
                          size="small"
                          onClick={useSimulatedValuesAsExpected}
                        >
                          Use Simulated Values as Expected
                        </Button>
                      )}
                    </Space>
                  }
                >
                  <Table
                    size="small"
                    pagination={false}
                    rowKey="testCaseId"
                    dataSource={draftTestResults}
                    expandable={{
                      expandedRowRender: (result) => (
                        <Table
                          size="small"
                          pagination={false}
                          rowKey="field"
                          dataSource={TEST_RESULT_FIELDS.filter(
                            ([field]) => result.expected?.[field] !== undefined,
                          ).map(([field, label]) => ({
                            field,
                            label,
                            expected: result.expected[field],
                            actual: result.actual[field],
                            variance: result.variance[field],
                          }))}
                          columns={[
                            { title: "Result", dataIndex: "label" },
                            { title: "Expected", dataIndex: "expected" },
                            { title: "Actual", dataIndex: "actual" },
                            {
                              title: "Variance",
                              dataIndex: "variance",
                              render: (value) => (
                                <Text
                                  type={
                                    Number(value) === 0 ? "success" : "danger"
                                  }
                                >
                                  {value}
                                </Text>
                              ),
                            },
                          ]}
                        />
                      ),
                    }}
                    columns={[
                      { title: "Test Case", dataIndex: "name" },
                      {
                        title: "Status",
                        dataIndex: "passed",
                        render: (value) => (
                          <Tag color={value ? "success" : "error"}>
                            {value ? "Passed" : "Failed"}
                          </Tag>
                        ),
                      },
                      {
                        title: "",
                        render: (_, result) => {
                          const failures = TEST_RESULT_FIELDS.filter(
                            ([field]) =>
                              result.variance?.[field] !== undefined &&
                              Number(result.variance[field]) !== 0,
                          ).map(([, label]) => label);
                          return failures.length
                            ? `Variance in ${failures.join(", ")}`
                            : "All expected values match";
                        },
                      },
                    ]}
                  />
                </Card>
              )}

              <Row gutter={12} style={{ marginTop: 20 }}>
                <Col span={12}>
                  <Form.Item
                    name="sourceReference"
                    label="Official source/reference"
                  >
                    <Input.TextArea rows={2} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="notes" label="Notes">
                    <Input.TextArea rows={2} />
                  </Form.Item>
                </Col>
              </Row>
            </>
          )}
        </Form>
      </Modal>

      <Modal
        title="Copy statutory rule version"
        open={Boolean(copySource)}
        onCancel={() => setCopySource(null)}
        onOk={submitCopy}
        confirmLoading={saving}
        okText="Create Draft version"
      >
        <Form form={copyForm} layout="vertical">
          <Form.Item
            name="name"
            label="New version name"
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="effectiveFrom"
            label="Effective from"
            rules={[{ required: true }]}
          >
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="effectiveTo" label="Effective to">
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={
          simulateRule
            ? `Simulator — ${simulateRule.name} v${simulateRule.versionNumber}`
            : "Rule simulator"
        }
        open={Boolean(simulateRule)}
        onCancel={() => setSimulateRule(null)}
        onOk={simulate}
        confirmLoading={saving}
        okText="Calculate"
        width={800}
      >
        <Form form={simulateForm} layout="vertical">
          <Alert
            type="info"
            style={{ marginBottom: 12 }}
            message="Enter gross earnings once. The simulator treats them as taxable and calculates PAYE chargeable income automatically after LST."
          />
          <Row gutter={12}>
            <Col span={8}>
              <Form.Item name="residencyStatus" label="Tax residency">
                <Select
                  options={["Resident", "Non-Resident"].map((value) => ({
                    value,
                    label: value,
                  }))}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="basicSalary"
                label="Basic salary"
                rules={[{ required: true }]}
              >
                <InputNumber
                  {...payrollAmountInputProps}
                  min={0}
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="grossEarnings"
                label="Gross earnings"
                rules={[{ required: true }]}
              >
                <InputNumber
                  {...payrollAmountInputProps}
                  min={0}
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="otherDeductions"
                label="Other post-PAYE deductions"
              >
                <InputNumber
                  {...payrollAmountInputProps}
                  min={0}
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="nssfEnabled"
                label="Employee NSSF enabled"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="lstEnabled"
                label="Employee LST enabled"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="lstMonth" label="Payroll month">
                <Select
                  options={Array.from({ length: 12 }, (_, month) => ({
                    value: month + 1,
                    label: dayjs().month(month).format("MMMM"),
                  }))}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
        {simulation && (
          <Descriptions bordered size="small" column={2}>
            <Descriptions.Item label="PAYE">
              UGX {Number(simulation.paye).toLocaleString()}
            </Descriptions.Item>
            <Descriptions.Item label="PAYE chargeable income">
              UGX {Number(simulation.chargeableIncome).toLocaleString()}
            </Descriptions.Item>
            <Descriptions.Item label="Employee NSSF">
              UGX {Number(simulation.employeeNssf).toLocaleString()}
            </Descriptions.Item>
            <Descriptions.Item label="Employer NSSF">
              UGX {Number(simulation.employerNssf).toLocaleString()}
            </Descriptions.Item>
            <Descriptions.Item label="Gross earnings used for LST">
              UGX {Number(simulation.lstAssessmentIncome).toLocaleString()}
            </Descriptions.Item>
            <Descriptions.Item label="LST calculation source">
              {simulation.lstAssessmentSource}
            </Descriptions.Item>
            <Descriptions.Item label="Annual LST liability">
              {simulation.lstConfigured
                ? `UGX ${Number(simulation.lstAnnualLiability).toLocaleString()}`
                : "Not configured"}
            </Descriptions.Item>
            <Descriptions.Item label="LST deducted">
              {simulation.lstConfigured
                ? `UGX ${Number(simulation.lst).toLocaleString()}`
                : "Not configured"}
            </Descriptions.Item>
            <Descriptions.Item label="Net pay">
              UGX {Number(simulation.netPay).toLocaleString()}
            </Descriptions.Item>
          </Descriptions>
        )}
        {simulation?.warnings?.length > 0 && (
          <Alert
            type="warning"
            showIcon
            style={{ marginTop: 12 }}
            message="Simulation used fallback data"
            description={simulation.warnings.join(" ")}
          />
        )}
      </Modal>

      <Modal
        title={
          historyRule
            ? `Lifecycle history — ${historyRule.name}`
            : "Lifecycle history"
        }
        open={Boolean(historyRule)}
        onCancel={() => setHistoryRule(null)}
        footer={null}
        width={760}
      >
        <Table
          rowKey="historyId"
          pagination={false}
          dataSource={historyRule?.history || []}
          columns={[
            {
              title: "When",
              dataIndex: "createdAt",
              render: (value) => dayjs(value).format("DD MMM YYYY HH:mm"),
            },
            { title: "Action", dataIndex: "action" },
            {
              title: "Change",
              render: (_, row) =>
                `${row.fromStatus || "—"} → ${row.toStatus || "—"}`,
            },
            { title: "User", dataIndex: "actorName" },
            {
              title: "Comment",
              dataIndex: "comment",
              render: (value) => value || "—",
            },
          ]}
        />
      </Modal>
    </Space>
  );
};

export default PayrollStatutoryRulesTab;
